import { z } from 'zod';

/** User.status enum mirrored from BE userStatusEnum (packages/db users.ts).
 *  PENDING_VERIFICATION = client self-registered, hasn't clicked the email-verify link yet.
 *  SHADOW = dormant staff-created record (helpline/offline capture), rendered as "Dormant". */
export const clientUserStatusEnum = z.enum([
  'ACTIVE',
  'PENDING_INVITE',
  'PENDING_VERIFICATION',
  'DISABLED',
  'SHADOW',
]);
export type ClientUserStatus = z.infer<typeof clientUserStatusEnum>;

export const clientTypeEnum = z.enum(['student', 'corporate']);
export type ClientTypeEnum = z.infer<typeof clientTypeEnum>;

export const genderEnum = z.enum(['male', 'female', 'other', 'unknown']);
export type Gender = z.infer<typeof genderEnum>;

/** Mirrors `employee_band` pgEnum in elixaa-core/packages/db/src/schema/clientProfiles.ts.
 *  Null for student clients. */
export const employeeBandEnum = z.enum(['ASSOCIATE', 'MID_SENIOR', 'SENIOR', 'LEADERSHIP']);
export type EmployeeBand = z.infer<typeof employeeBandEnum>;

/** BE riskProfile values. Must mirror the DB enum
 *  (`elixaa-core/packages/db/src/schema/clientProfiles.ts`) — missing values
 *  cause Zod parse failures on every API response that includes a client with
 *  that risk level. */
export const riskProfileEnum = z.enum([
  'critical',
  'very_high',
  'high',
  'medium',
  'low',
  'very_low',
  'none',
  'unknown',
]);
export type RiskProfile = z.infer<typeof riskProfileEnum>;

/**
 * Client record. BE returns user fields FLAT alongside profile fields
 * (clientService.ts:255-380 + getClientById:417-449). Not nested under `user`.
 * `riskProfile` is stripped server-side for client-role callers; admin always sees it.
 */
export const clientSchema = z.object({
  userId: z.string(),
  // Null on SHADOW (dormant) rows captured phone-only; phoneNumber then holds
  // the bare 10-digit number.
  email: z.string().nullable(),
  firstName: z.string(),
  lastName: z.string(),
  phoneNumber: z.string().nullish(),
  status: clientUserStatusEnum,
  profilePicture: z.string().nullish(),
  businessAssociateId: z.string().nullish(),
  organizationId: z.string().nullable(),
  organizationName: z.string().nullish(),
  locationId: z.string().nullable(),
  locationName: z.string().nullish(),
  // Location feature gate for the student chat surface, denormalized onto the
  // detail response (GET /clients/:id) since that's the only client-readable
  // payload carrying the client's location. Nullish: null when the client has
  // no location; absent on the list endpoint.
  chatSupport: z.boolean().nullish(),
  clientType: clientTypeEnum,
  gender: genderEnum.nullish(),
  metadata: z.record(z.string(), z.unknown()).nullish(),
  // Typed analytics columns on ClientProfile. Student-only fields (programme,
  // year) are null for corporate clients; employeeBand is null for students.
  // `department` is universal. Both name columns are free-text snapshots of the
  // location catalog rows picked at onboarding, not enums.
  department: z.string().nullish(),
  programme: z.string().nullish(),
  year: z.number().int().nullish(),
  employeeBand: employeeBandEnum.nullish(),
  riskProfile: riskProfileEnum.nullish(),
  // Present on GET /clients rows for coping-type readers only.
  copingTypes: z.array(z.string()).optional(),
  referralSource: z.string().nullish(),
  // When true, the client is left out of org insights/analytics but still logs
  // in and takes sessions normally. Nullish so rows not yet backfilled parse.
  excludeFromInsights: z.boolean().nullish(),
  createdAt: z.string(),
  updatedAt: z.string().nullish(),
});
export type Client = z.infer<typeof clientSchema>;

/** PHI fetch shape — GET /clients/:id/phi. */
export const clientPhiSchema = z.object({
  userId: z.string(),
  dateOfBirth: z.string().nullable(),
  emergencyContactName: z.string().nullable(),
  emergencyContactPhone: z.string().nullable(),
  emergencyContactRelation: z.string().nullable(),
});
export type ClientPhi = z.infer<typeof clientPhiSchema>;

/**
 * Non-PHI update payload for PATCH /clients/:id.
 * BE accepts only this subset (clientSchema.ts:47-71): locationId, clientType,
 * gender, metadata, riskProfile, referralSource. (PHI fields belong to the
 * separate updateClientPhiSchema below.) phoneNumber, profilePicture,
 * organizationId, email, firstName, lastName are NOT accepted by PATCH.
 */
export const updateClientNonPhiSchema = z.object({
  locationId: z.string().max(50).optional(),
  clientType: clientTypeEnum.optional(),
  gender: genderEnum.optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  riskProfile: riskProfileEnum.optional(),
  referralSource: z.string().max(255).optional().or(z.literal('')),
  excludeFromInsights: z.boolean().optional(),
});
export type UpdateClientNonPhiInput = z.infer<typeof updateClientNonPhiSchema>;

/** PHI update payload — same endpoint, partitioned client-side for audit clarity. */
export const updateClientPhiSchema = z.object({
  dateOfBirth: z.string().optional(),
  emergencyContactName: z.string().max(255).optional().or(z.literal('')),
  emergencyContactPhone: z
    .string()
    .max(20)
    .regex(/^\+?[\d\s\-()]+$/, 'Use digits, spaces, dashes or parentheses')
    .optional()
    .or(z.literal('')),
  emergencyContactRelation: z.string().max(100).optional().or(z.literal('')),
});
export type UpdateClientPhiInput = z.infer<typeof updateClientPhiSchema>;

/**
 * List query params accepted by BE (clientSchema.ts:107-119).
 * NOT accepted: status (filter client-side), businessAssociateId (BE auto-scopes
 * by requester's BA; super_admin sees cross-BA implicitly).
 */
export interface ClientListParams {
  page?: number;
  limit?: number;
  sortBy?: 'createdAt' | 'updatedAt' | 'firstName';
  sortOrder?: 'asc' | 'desc';
  organizationId?: string;
  locationId?: string;
  // Display types, not just the stored enum: 'staff' / 'faculty' filter
  // clientType='student' rows by their metadata affiliation server-side
  // (mirrors resolveClientDisplayType in ../utils/clientDisplayType).
  clientType?: ClientTypeEnum | 'staff' | 'faculty';
  riskProfile?: RiskProfile;
  department?: string;
  yearOfStudy?: string;
  search?: string;
  mine?: boolean;
}

export interface PaginatedClients {
  data: Client[];
  pagination: { page: number; limit: number; total: number };
}

// ─── Nested resources (read-only on the client detail tabs) ──────────────────

export const flagTypeEnum = z.enum(['crisis', 'acute_risk']);
export type FlagType = z.infer<typeof flagTypeEnum>;

export const redFlagStatusEnum = z.enum(['open', 'under_review', 'closed']);
export type RedFlagStatus = z.infer<typeof redFlagStatusEnum>;

/** Raw RedFlag row from BE (db/schema/redFlags.ts). description is encrypted PHI server-side. */
export const redFlagSchema = z.object({
  id: z.string(),
  clientId: z.string(),
  sessionId: z.string().nullish(),
  raisedById: z.string(),
  flagType: flagTypeEnum,
  status: redFlagStatusEnum,
  description: z.string().nullable(),
  assignedToId: z.string().nullish(),
  actionTaken: z.string().nullish(),
  resolvedAt: z.string().nullish(),
  resolvedById: z.string().nullish(),
  createdAt: z.string(),
  updatedAt: z.string().nullish(),
  // Denormalized actor display names joined by the BE — raiser (always when
  // present in the User table) and resolver (only on closed flags). Both
  // nullish so legacy rows + soft-deleted authors still parse.
  raisedByName: z.string().nullish(),
  resolvedByName: z.string().nullish(),
});
export type RedFlag = z.infer<typeof redFlagSchema>;

export const assignmentStatusEnum = z.enum(['pending', 'in_progress', 'completed']);
export type AssignmentStatus = z.infer<typeof assignmentStatusEnum>;

/**
 * Row shape from GET /clients/:clientId/assessments (assessmentService:listClientAssessments).
 * Joins AssessmentAssignment with Assessment template fields.
 */
export const clientAssessmentSchema = z.object({
  id: z.string(),
  assessmentId: z.string(),
  status: assignmentStatusEnum,
  dueDate: z.string().nullish(),
  createdAt: z.string(),
  title: z.string(),
  /** Short client-facing name ("Stress & Anxiety Check"). The clinical `title`
   *  runs long and wraps; client surfaces render `displayName ?? title`.
   *  Nullish — templates nobody has named yet, and any backend that predates
   *  the column, simply fall back to `title`. */
  displayName: z.string().nullish(),
  description: z.string().nullish(),
  templateCode: z.string().nullish(),
  isStandard: z.boolean().nullish(),
  scoringType: z.string().nullish(),
  /** Presigned (or plain) card artwork URL. Null for templates without art —
   *  the card falls back to `ContentCover`'s typographic/glyph cover. */
  thumbnailDownloadUrl: z.string().nullish(),
  // Card artwork COMPOSITION — contained on the card surface (illustration) vs
  // full-bleed (photograph/still). Recorded per row at upload time; null means
  // "not recorded" and reads as full-bleed, i.e. the behaviour that predates
  // the column. See `coverFitFor`.
  thumbnailIsIllustration: z.boolean().nullish(),
});
export type ClientAssessment = z.infer<typeof clientAssessmentSchema>;

// ─── Dormant client quick-create ──────────────────────────────────────────

/** Input for POST /clients/shadow (dormant client quick-create). At least one
 *  of email/phone; BE requires org + location and normalizes both keys. */
export const createDormantClientInputSchema = z
  .object({
    name: z.string().trim().max(200).optional(),
    email: z.string().trim().pipe(z.email('Enter a valid email')).optional().or(z.literal('')),
    phoneNumber: z
      .string()
      .regex(/^\d{10}$/, 'Enter the 10-digit mobile number')
      .optional()
      .or(z.literal('')),
    organizationId: z.string().min(1),
    locationId: z.string().min(1),
  })
  .refine((v) => Boolean(v.email) || Boolean(v.phoneNumber), {
    // Both contact fields render on the same dialog row, so this single error
    // (path ['email']) is the one the dialog should surface near either field.
    message: 'Add an email or a phone number',
    path: ['email'],
  });
export type CreateDormantClientInput = z.infer<typeof createDormantClientInputSchema>;

export const createDormantClientResultSchema = z.object({
  userId: z.string(),
  organizationId: z.string(),
  locationId: z.string(),
});
export type CreateDormantClientResult = z.infer<typeof createDormantClientResultSchema>;
